import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldAlert, X } from "lucide-react";
import { BANNER_VERSIONS } from "@/lib/banner-versions";

const STORAGE_KEY = BANNER_VERSIONS.jobSafety.currentKey;
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

function readDismissed(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < ONE_YEAR_MS;
  } catch {
    return false;
  }
}

export default function JobSafetyBanner() {
  const [visible, setVisible] = useState<boolean>(() => !readDismissed());

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Non-fatal
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={false}
          animate={{ height: "auto", opacity: 1, marginBottom: 24 }}
          exit={{ height: 0, opacity: 0, marginBottom: 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-amber-950/40 border border-amber-700/40 text-amber-200/80 font-sans text-sm">
            <ShieldAlert className="w-4 h-4 text-amber-500/80 shrink-0 mt-0.5" />
            <p className="flex-1 leading-relaxed text-amber-200/70">
              <span className="font-semibold text-amber-300/90">Safety First:</span>{" "}
              Although only Carry Network members can action on them, anyone can post a job to this board. We do not verify external job links.
              Always apply through official company portals, exercise caution with unverified contacts, and report inappropriate or misleading listings.
            </p>
            <button
              onClick={handleDismiss}
              className="shrink-0 text-amber-500/60 hover:text-amber-300 transition-colors mt-0.5"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
